import {SystemEntity, ArtfactKind} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import axios, {AxiosRequestConfig, AxiosResponse, AxiosError} from 'axios';
import * as https from 'https'; // For https.Agent
import * as http from 'http';   // For http.Agent (less common but good for consistency)
import * as fs from 'fs';
import {v4 as uuid} from 'uuid';
import {Observable, Observer} from 'rxjs';
import {concatMap} from 'rxjs/operators';
import moment from 'moment'; // Assuming moment is still desired for date formatting in buildRoute
import FormData from 'form-data';

// Define a type for the configuration object passed in NickProcessArtfact
interface RequestConfig {
    url: string;
    headers?: { [key: string]: any };
    params?: { [key: string]: any };
    body?: any;
    statusError?: any,
    json?: boolean; // For axios, this is usually inferred or handled by body type
    rejectUnauthorized?: boolean;
    requestCert?: boolean; // Not directly used by axios for client cert, but for agent config
    pfx?: Buffer | string; // PFX certificate content
    passphrase?: string; // PFX or private key passphrase
    ca?: Buffer | string | (Buffer | string)[]; // CA certificate(s)
    isDownload?: boolean;
    gzip?: boolean; // Axios handles gzip automatically
    timeout?: number;
    fileKey?: string; // For multipart/form-data file upload
}

@HookClass('http.axios')
export class HttpAxiosHook extends BaseHook {

    @Hook('f5e8d7c1-a2b3-4c5d-9e0f-1a2b3c4d5e6f') // New UUID
    public get(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.system.pipe(concatMap((system: SystemEntity) => {
            const config: RequestConfig = {
                url: `${inputArtifact.object.config.url}`,
                headers: inputArtifact.object.config.headers || {},
                params: inputArtifact.object.config.params,
                gzip: inputArtifact.object.config?.gzip,
                isDownload: inputArtifact.object.config?.isDownload,
                rejectUnauthorized: inputArtifact.object.config?.rejectUnauthorized,
                requestCert: inputArtifact.object.config?.requestCert,
                ca: inputArtifact.object.config?.ca,
                pfx: inputArtifact.object.config?.pfx,
                passphrase: inputArtifact.object.config?.passphrase,
                timeout: inputArtifact.object.config?.timeout
            };
            config.headers!['nick-system'] = system.id;
            config.headers!['authorization'] = this.token;

            return this._makeRequest('GET', config, inputArtifact);
        }));
    }

    @Hook('e1d2c3b4-a5f6-7e8d-9c0b-1a2b3c4d5e6f') // New UUID
    public postExternal(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const config: RequestConfig = {
            url: `${inputArtifact.object.config.url}`,
            headers: inputArtifact.object.config.headers || {},
            params: inputArtifact.object.config.params,
            body: inputArtifact.object.config.body,
            json: inputArtifact.object.config?.hasOwnProperty('json') ? inputArtifact.object.config.json : true,
            rejectUnauthorized: inputArtifact.object.config?.rejectUnauthorized,
            requestCert: inputArtifact.object.config?.requestCert,
            pfx: inputArtifact.object.config?.pfx,
            passphrase: inputArtifact.object.config?.passphrase,
            ca: inputArtifact.object.config?.ca,
            isDownload: inputArtifact.object.config?.isDownload,
            gzip: inputArtifact.object.config?.gzip,
            timeout: inputArtifact.object.config?.timeout,
            fileKey: inputArtifact.object.config?.fileKey
        };
        return this._makeRequest('POST', config, inputArtifact);
    }

    @Hook('d2c3b4a5-f6e7-8d9c-0b1a-2b3c4d5e6f7a') // New UUID
    public getExternal(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const config: RequestConfig = {
            url: `${inputArtifact.object.config.url}`,
            headers: inputArtifact.object.config.headers || {},
            params: inputArtifact.object.config.params,
            gzip: inputArtifact.object.config?.gzip,
            isDownload: inputArtifact.object.config?.isDownload,
            rejectUnauthorized: inputArtifact.object.config?.rejectUnauthorized,
            requestCert: inputArtifact.object.config?.requestCert,
            ca: inputArtifact.object.config?.ca,
            pfx: inputArtifact.object.config?.pfx,
            passphrase: inputArtifact.object.config?.passphrase,
            timeout: inputArtifact.object.config?.timeout
        };
        return this._makeRequest('GET', config, inputArtifact);
    }

    @Hook('c3b4a5f6-e7d8-9c0b-1a2b-3c4d5e6f7a8b') // New UUID
    public deleteExternal(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const config: RequestConfig = {
            url: `${inputArtifact.object.config.url}`,
            headers: inputArtifact.object.config.headers || {},
            params: inputArtifact.object.config.params,
            rejectUnauthorized: inputArtifact.object.config?.rejectUnauthorized,
            requestCert: inputArtifact.object.config?.requestCert,
            ca: inputArtifact.object.config?.ca,
            pfx: inputArtifact.object.config?.pfx,
            passphrase: inputArtifact.object.config?.passphrase,
            timeout: inputArtifact.object.config?.timeout
        };
        return this._makeRequest('DELETE', config, inputArtifact);
    }


    // --- Public Hook Methods (Updated for Axios) ---

    @Hook('b4a5f6e7-d8c9-0b1a-2b3c-4d5e6f7a8b9c') // New UUID
    public patchExternal(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const config: RequestConfig = {
            url: `${inputArtifact.object.config.url}`,
            headers: inputArtifact.object.config.headers || {},
            params: inputArtifact.object.config.params,
            body: inputArtifact.object.config.body,
            json: inputArtifact.object.config?.hasOwnProperty('json') ? inputArtifact.object.config.json : true,
            rejectUnauthorized: inputArtifact.object.config?.rejectUnauthorized,
            requestCert: inputArtifact.object.config?.requestCert,
            pfx: inputArtifact.object.config?.pfx,
            passphrase: inputArtifact.object.config?.passphrase,
            ca: inputArtifact.object.config?.ca,
            timeout: inputArtifact.object.config?.timeout
        };
        return this._makeRequest('PATCH', config, inputArtifact);
    }

    @Hook('a5f6e7d8-c9b0-1a2b-3c4d-5e6f7a8b9c0d') // New UUID
    public putExternal(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const config: RequestConfig = {
            url: `${inputArtifact.object.config.url}`,
            headers: inputArtifact.object.config.headers || {},
            params: inputArtifact.object.config.params,
            body: inputArtifact.object.config.body,
            json: inputArtifact.object.config?.hasOwnProperty('json') ? inputArtifact.object.config.json : true,
            rejectUnauthorized: inputArtifact.object.config?.rejectUnauthorized,
            requestCert: inputArtifact.object.config?.requestCert,
            pfx: inputArtifact.object.config?.pfx,
            passphrase: inputArtifact.object.config?.passphrase,
            ca: inputArtifact.object.config?.ca,
            fileKey: inputArtifact.object.config?.fileKey,
            timeout: inputArtifact.object.config?.timeout
        };
        return this._makeRequest('PUT', config, inputArtifact);
    }

    /**
     * Centralized method to create an HTTPS/HTTP agent based on configuration.
     * Handles PFX, CA, and rejectUnauthorized options.
     * @param config The request configuration object.
     * @returns An https.Agent or http.Agent instance, or undefined if no special agent is needed.
     */
    private createAgent(config: RequestConfig): https.Agent | http.Agent | undefined {
        let agentOptions: any = {};

        // Handle PFX certificate for client authentication
        if (config.pfx) {
            agentOptions.pfx = config.pfx;
            if (config.passphrase) {
                agentOptions.passphrase = config.passphrase;
            }
        }

        // Handle CA certificate for trusting custom/self-signed server certificates
        if (config.ca) {
            agentOptions.ca = config.ca;
        }

        // Handle SSL certificate validation (use with caution in production)
        if (config.rejectUnauthorized !== undefined) {
            agentOptions.rejectUnauthorized = config.rejectUnauthorized;
        }

        // If no specific options are set, return undefined to let axios use default agent
        if (Object.keys(agentOptions).length === 0) {
            return undefined;
        }

        // Determine protocol based on URL for the agent type
        const protocol = config.url.startsWith('https') ? https : http;
        return new protocol.Agent(agentOptions);
    }

    /**
     * Generic method to make an Axios HTTP request and map its response to an Observable.
     * Handles response parsing, error mapping, and download streams.
     * @param method The HTTP method (GET, POST, PUT, PATCH, DELETE).
     * @param config The request configuration.
     * @param inputArtifact The original input artifact.
     * @returns An Observable of NickProcessArtfact.
     */
    private _makeRequest(method: AxiosRequestConfig['method'], config: RequestConfig, inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return new Observable((observer: Observer<NickProcessArtfact>) => {
            const axiosConfig: AxiosRequestConfig = {
                method: method,
                url: this.buildRoute(config.url, config.params).toString(), // Convert URL object to string
                headers: this.buildAuthorization(config.headers || {}),
                timeout: config.timeout,
                // Axios automatically handles gzip compression/decompression
                // responseType: config.isDownload ? 'stream' : 'json', // Default to json for non-downloads
                httpsAgent: this.createAgent(config) // Pass the custom agent
            };

            // Handle request body for POST, PUT, PATCH
            if (config.body && ['POST', 'PUT', 'PATCH'].includes(method as string)) {
                const contentType = (config.headers && config.headers['Content-Type']) ? config.headers['Content-Type'].toLowerCase() : '';

                if (contentType.includes('x-www-form-urlencoded')) {
                    // Axios expects URLSearchParams or a plain object for form data
                    axiosConfig.data = new URLSearchParams(config.body).toString();
                    axiosConfig.headers!['Content-Type'] = 'application/x-www-form-urlencoded';
                } else if (contentType.includes('multipart/form-data')) {
                    // Use 'form-data' package for multipart/form-data in Node.js
                    const formData = new FormData();
                    for (const key in config.body) {
                        if (config.body.hasOwnProperty(key)) {
                            // Check if it's the file object based on fileKey
                            if (config.fileKey && key === config.fileKey && config.body[key]?.File?.path) {
                                // Append file stream directly
                                formData.append(key, fs.createReadStream(config.body[key].File.path), {
                                    filename: config.body[key].File.name,
                                    contentType: config.body[key].File.type
                                });
                            } else {
                                formData.append(key, config.body[key]);
                            }
                        }
                    }
                    axiosConfig.data = formData;
                    // Axios will set the correct multipart/form-data boundary header automatically
                    delete axiosConfig.headers!['Content-Type']; // Let Axios handle this
                } else {
                    // Default to JSON body
                    axiosConfig.data = config.body;
                    // If json is explicitly false, ensure Content-Type is not application/json
                    if (config.hasOwnProperty('json') && config.json === false) {
                        // User wants raw body, ensure Content-Type is not JSON unless specified
                        if (!axiosConfig.headers!['Content-Type']) {
                            axiosConfig.headers!['Content-Type'] = 'text/plain'; // Or whatever default is appropriate
                        }
                    } else if (!axiosConfig.headers!['Content-Type']) {
                        axiosConfig.headers!['Content-Type'] = 'application/json'; // Default to JSON
                    }
                }
            }

            // Handle download specific logic
            if (config.isDownload) {
                axiosConfig.responseType = 'stream';
                // For downloads, it's common to relax unauthorized for internal systems, but be careful
                if (axiosConfig.httpsAgent && typeof (axiosConfig.httpsAgent as https.Agent).options === 'object') {
                    (axiosConfig.httpsAgent as https.Agent).options.rejectUnauthorized = config.rejectUnauthorized !== undefined ? config.rejectUnauthorized : false;
                } else if (config.rejectUnauthorized !== undefined) {
                    // Fallback if agent not created or options not accessible directly
                    axiosConfig.httpsAgent = new https.Agent({rejectUnauthorized: config.rejectUnauthorized});
                } else {
                    axiosConfig.httpsAgent = new https.Agent({rejectUnauthorized: false}); // Default for downloads
                }
            }


            axios(axiosConfig)
                .then(async (response: AxiosResponse) => {
                    const outputArtifact = new NickProcessArtfact();
                    outputArtifact.object = {}; // Initialize object

                    if (config.isDownload && response.data) {
                        // For downloads, pipe the stream to a temporary file
                        const fileName = `/tmp/${uuid()}`;
                        const writer = fs.createWriteStream(fileName);
                        response.data.pipe(writer);

                        writer.on('finish', () => {
                            outputArtifact.object = fs.createReadStream(fileName);
                            observer.next(outputArtifact);
                            observer.complete();
                        });
                        writer.on('error', (err) => {
                            observer.error(err);
                            observer.complete();
                        });
                    } else {
                        // For non-downloads, parse response body
                        let responseData = response.data;

                        // Axios usually auto-parses JSON. If not, or if it's a string, try parsing.
                        if (typeof responseData === 'string') {
                            try {
                                const parsed = JSON.parse(responseData);
                                // Check if parsed is a number but original was string (e.g., "123" parsed to 123)
                                // If so, keep original string if that's the intent, otherwise use parsed
                                if (typeof parsed === 'number' && parsed.toString() !== responseData) {
                                    outputArtifact.object = responseData; // Keep original string if it was a number string
                                } else {
                                    outputArtifact.object = parsed;
                                }
                            } catch (e) {
                                outputArtifact.object = responseData; // Not JSON, keep as string
                            }
                        } else {
                            outputArtifact.object = responseData; // Already an object (e.g., parsed JSON)
                        }

                        // Check for status errors
                        if (config.statusError instanceof Array && config.statusError.includes(response.status)) {
                            outputArtifact.kind = ArtfactKind.ERROR;
                            outputArtifact.object = {
                                message: outputArtifact.object,
                                statusCode: response.status,
                                statusMessage: response.statusText
                            };
                            observer.error(outputArtifact);
                        } else {
                            // Success path
                            if (response.status >= 200 && response.status <= 409) {
                                observer.next(outputArtifact);
                            } else {
                                outputArtifact.kind = ArtfactKind.ERROR;
                                outputArtifact.object = {
                                    message: outputArtifact.object,
                                    statusCode: response.status,
                                    statusMessage: response.statusText
                                };
                                observer.error(outputArtifact);
                            }
                        }
                        observer.complete();
                    }
                })
                .catch((error: AxiosError) => {
                    const outputArtifact = new NickProcessArtfact();
                    outputArtifact.kind = ArtfactKind.ERROR;

                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        outputArtifact.object = {
                            message: error.response.data,
                            statusCode: error.response.status,
                            statusMessage: error.response.statusText,
                            headers: error.response.headers
                        };
                    } else if (error.request) {
                        // The request was made but no response was received
                        outputArtifact.object = {
                            message: 'No response received from server',
                            error: error.message
                        };
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        outputArtifact.object = {
                            message: 'Error setting up request',
                            error: error.message
                        };
                    }
                    observer.error(outputArtifact);
                    observer.complete();
                });
        });
    }

    // Existing buildAuthorization method (no changes needed)
    private buildAuthorization(headers: any): any {
        if (headers && (headers.hasOwnProperty('Authorization') || headers.hasOwnProperty('authorization'))) {
            const authorization = headers['Authorization'] || headers['authorization'];
            if (authorization && typeof authorization != 'string') {
                switch (authorization.type.toLowerCase()) {
                    case 'basic': {
                        const authHeader = Buffer.from(`${authorization.username}:${authorization.password}`).toString('base64');
                        delete headers['Authorization'];
                        delete headers['authorization'];
                        headers['authorization'] = `Basic ${authHeader}`;
                        break;
                    }
                    case 'bearer': {
                        delete headers['Authorization'];
                        delete headers['authorization'];
                        headers['authorization'] = `Bearer ${authorization.token}`;
                        break;
                    }
                }
            }
        }
        return headers;
    }

    // Existing buildRoute method (no changes needed)
    private buildRoute(url: string, params?: any): URL {
        const _url = new URL('https://dummy'); // Dummy base URL for URLSearchParams

        if (params) {
            Object.keys(params).forEach((key: string) => {
                if (url.indexOf(`{{${key}}}`) > -1) {
                    url = url.replace(`{{${key}}}`, params[key]);
                } else {
                    if (params[key] !== undefined && params[key] != null) {
                        const value = params[key];

                        if (!(value instanceof Array) && value instanceof Object && Object.keys(value).length) {
                            Object.keys(value).forEach((insideKey) => {
                                if (value[insideKey] !== undefined) {
                                    let skipAppend = false;
                                    if (value[insideKey] instanceof Array) {
                                        value[insideKey] = value[insideKey].map((val: any) => {
                                            if (val instanceof Date)
                                                return moment(val).parseZone().format();
                                            if (!isNaN(val))
                                                return val;
                                            if (typeof val === 'string')
                                                return val;
                                            if (val instanceof Object) {
                                                skipAppend = true;
                                                _url.searchParams.append(insideKey, JSON.stringify(val));
                                            }
                                            return val;
                                        });
                                    } else if (value[insideKey] instanceof Object) {
                                        value[insideKey] = JSON.stringify(value[insideKey]);
                                    }

                                    if (!skipAppend)
                                        _url.searchParams.append(insideKey, value[insideKey]);
                                }
                            });
                        } else {
                            _url.searchParams.append(key, params[key]);
                        }
                    }
                }
            });
        }

        const finalUrl = new URL(url);
        _url.searchParams.forEach((value: string, key: string) => finalUrl.searchParams.append(key, value));
        return finalUrl;
    }
}
